/**
 * Campo de partículas do Nexus (HeroNexus / Particle Nebula).
 * Baseado em packages/ui/src/patterns/HeroNexus.tsx do @nexus-system/ui.
 */
import React, { useEffect, useRef } from 'react';

const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

export default function NexusParticleField() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const hero = canvas.parentElement;
    if (!hero) return;

    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced) {
      return undefined;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    let W = 0;
    let H = 0;
    let clusters = [];
    let isMobile = false;
    let scrollFactor = 0;
    const pointer = { x: 0.5, y: 0.5, active: false };

    function rand(a, b) {
      return a + Math.random() * (b - a);
    }

    function lerpColor(c1, c2, t) {
      const r = Math.round(c1[0] + (c2[0] - c1[0]) * t);
      const g = Math.round(c1[1] + (c2[1] - c1[1]) * t);
      const b = Math.round(c1[2] + (c2[2] - c1[2]) * t);
      return `rgba(${r},${g},${b},`;
    }

    const brand = [0, 158, 219];
    const navy = [17, 29, 48];
    const grey = [180, 188, 196];
    const white = [255, 255, 255];

    function buildClusters() {
      clusters = [];
      const particleTarget = isMobile ? 2400 : 4200;
      const clusterCount = isMobile ? 4 : 6;
      const perCluster = Math.round(particleTarget / clusterCount);
      for (let i = 0; i < clusterCount; i += 1) {
        const depth = i / (clusterCount - 1 || 1);
        const centerX = W * (0.05 + 0.9 * Math.random());
        const isTopBand = i < clusterCount / 3;
        const bandTop = isTopBand ? 0.05 : 0.5;
        const bandHeight = isTopBand ? 0.4 : 0.45;
        const centerY = H * (bandTop + bandHeight * Math.random());
        const maxSide = Math.max(W, H);
        const baseRadius = maxSide * rand(0.22, 0.38);
        const cycle = [7, 9, 6, 10, 8, 11][i % 6];
        const cluster = {
          cx: centerX,
          cy: centerY,
          depth,
          baseRadius,
          phase: Math.random() * Math.PI * 2,
          speed: (Math.PI * 2) / (cycle * 1000),
          particles: [],
        };
        for (let j = 0; j < perCluster; j += 1) {
          const angle = Math.random() * Math.PI * 2;
          const radius = baseRadius * Math.pow(Math.random(), 0.6);
          const sizeBase = Math.random() < 0.8 ? rand(0.35, 1.2) : rand(1.3, 2.2);
          const glow = sizeBase > 1.55;
          const mix = Math.random();
          let cFrom;
          let cTo;
          if (mix < 0.35) {
            cFrom = brand;
            cTo = navy;
          } else if (mix < 0.7) {
            cFrom = brand;
            cTo = grey;
          } else {
            cFrom = grey;
            cTo = white;
          }
          const col = lerpColor(cFrom, cTo, Math.random());
          const reactAngle = angle + rand(-Math.PI / 2, Math.PI / 2);
          cluster.particles.push({
            bx: Math.cos(angle) * radius,
            by: Math.sin(angle) * radius,
            jitterX: rand(-3, 3),
            jitterY: rand(-3, 3),
            jitterSpeed: rand(0.00016, 0.00042),
            sizeBase,
            glow,
            colorBase: col,
            rx: Math.cos(reactAngle),
            ry: Math.sin(reactAngle),
          });
        }
        clusters.push(cluster);
      }
    }

    function resize() {
      const rect = hero.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      W = canvas.width = rect.width * dpr;
      H = canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      isMobile = window.innerWidth < 768;
      buildClusters();
    }

    let lastTime = performance.now();
    let frameId;

    function draw(now) {
      const dt = now - lastTime;
      lastTime = now;
      void dt;

      ctx.clearRect(0, 0, W, H);

      const grad = ctx.createRadialGradient(W / 2, H, 0, W / 2, H, H * 1.15);
      grad.addColorStop(0, 'rgba(0,158,219,.035)');
      grad.addColorStop(0.5, 'rgba(17,29,48,.018)');
      grad.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);

      const px = pointer.x * W;
      const py = pointer.y * H;

      clusters.forEach((cl, idx) => {
        const t = now + cl.phase * 1000;
        const breath = 1 + 0.055 * Math.sin(t * cl.speed);
        const parallax = scrollFactor * 28 * (0.2 + 0.8 * (1 - cl.depth));
        const frontOffset = (idx < 2 ? 1 : -1) * scrollFactor * 14 * cl.depth;
        const pointerInfluence = pointer.active ? 18 : 9;
        const pxOff = (pointer.x - 0.5) * pointerInfluence * (1 - cl.depth);
        const pyOff = (pointer.y - 0.5) * pointerInfluence * (1 - cl.depth);

        cl.particles.forEach((p, i) => {
          const jitterPhase = t * p.jitterSpeed + i;
          const jx = Math.sin(jitterPhase) * p.jitterX;
          const jy = Math.cos(jitterPhase * 1.3) * p.jitterY;
          let x = cl.cx + cl.depth * frontOffset + pxOff + p.bx * breath + jx;
          let y = cl.cy + parallax + pyOff + p.by * breath + jy;
          const size = p.sizeBase * (1 + 0.14 * Math.sin(jitterPhase * 0.8));

          if (pointer.active) {
            const dx = x - px;
            const dy = y - py;
            const d = Math.sqrt(dx * dx + dy * dy) || 1;
            const influenceRadius = W * 0.11;
            if (d < influenceRadius) {
              const influence = 1 - d / influenceRadius;
              const wave = 0.5 + 0.5 * Math.sin(jitterPhase * 0.5);
              const push = 5 * influence * influence * wave;
              x += p.rx * push;
              y += p.ry * push;
            }
          }

          const alpha = 0.22 + 0.5 * (1 - cl.depth);
          if (p.glow) {
            const g = ctx.createRadialGradient(x, y, 0, x, y, size * 5);
            g.addColorStop(0, `${p.colorBase}${alpha})`);
            g.addColorStop(1, `${p.colorBase}0)`);
            ctx.fillStyle = g;
            ctx.beginPath();
            ctx.arc(x, y, size * 5, 0, Math.PI * 2);
            ctx.fill();
          }
          ctx.fillStyle = `${p.colorBase}${alpha + 0.12})`;
          ctx.beginPath();
          ctx.arc(x, y, size, 0, Math.PI * 2);
          ctx.fill();
        });
      });

      frameId = window.requestAnimationFrame(draw);
    }

    function handleResize() {
      resize();
    }

    function handleScroll() {
      const max = Math.max(1, document.body.scrollHeight - window.innerHeight);
      scrollFactor = window.scrollY / max;
    }

    function handleMove(e) {
      const rect = hero.getBoundingClientRect();
      pointer.x = (e.clientX - rect.left) / rect.width;
      pointer.y = (e.clientY - rect.top) / rect.height;
      pointer.active = true;
    }

    function handleLeave() {
      pointer.active = false;
    }

    resize();
    frameId = window.requestAnimationFrame(draw);
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, { passive: true });
    hero.addEventListener('mousemove', handleMove);
    hero.addEventListener('mouseleave', handleLeave);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
      hero.removeEventListener('mousemove', handleMove);
      hero.removeEventListener('mouseleave', handleLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className="pointer-events-none absolute inset-0 z-0 block h-full w-full"
      style={{ transition: `opacity 400ms ${EASE}` }}
    />
  );
}
